/* Dynamic DNS hosts.
 *
 * ⚠ The update token is returned ONCE, on create and on rotate. The backend
 * stores only a hash of it (dns_dynamic_hosts.token_hash), so it genuinely
 * cannot be shown again -- the UI must say so plainly rather than let someone
 * close the dialog expecting to find it later.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type DynamicHost, type DynamicHostCreated } from "../api";

export function DynamicHosts({ zoneId }: { zoneId: string }) {
  const queryClient = useQueryClient();
  const [fqdn, setFqdn] = useState("");
  const [freshToken, setFreshToken] = useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["dns", "zone", zoneId, "dynamic-hosts"] });

  const hosts = useQuery({
    queryKey: ["dns", "zone", zoneId, "dynamic-hosts"],
    queryFn: () => api.get<DynamicHost[]>(`/api/dns/zones/${zoneId}/dynamic-hosts`),
  });

  const create = useMutation({
    mutationFn: (name: string) =>
      api.post<DynamicHostCreated>(`/api/dns/zones/${zoneId}/dynamic-hosts`, {
        fqdn: name.trim(),
      }),
    onSuccess: (created) => {
      setFreshToken(created.token);
      setFqdn("");
      invalidate();
    },
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<DynamicHostCreated>(`/api/dns/dynamic-hosts/${id}/rotate`),
    onSuccess: (rotated) => {
      setFreshToken(rotated.token);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/dns/dynamic-hosts/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div>
      <h2>Dynamic hosts</h2>

      {freshToken && (
        <div className="panel warn">
          <p>
            <strong>Copy this token now. It will not be shown again</strong> — we store only a
            hash of it, so it cannot be recovered. Rotating issues a new one and invalidates
            this.
          </p>
          <code>{freshToken}</code>
          <button onClick={() => setFreshToken(null)}>I have copied it</button>
        </div>
      )}

      {hosts.isLoading && <p className="muted">Loading…</p>}
      {hosts.isError && <p className="bad">{(hosts.error as Error).message}</p>}

      {!hosts.isLoading && !hosts.isError && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Host</th>
                <th>Last IP</th>
                <th>Last update</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(hosts.data ?? []).map((host) => (
                <tr key={host.id}>
                  <td>{host.fqdn}</td>
                  <td>{host.last_ip ?? "never updated"}</td>
                  <td>
                    {host.last_update_at ? new Date(host.last_update_at).toLocaleString() : "—"}
                  </td>
                  <td>
                    <button onClick={() => rotate.mutate(host.id)} disabled={rotate.isPending}>
                      Rotate token
                    </button>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete the dynamic host ${host.fqdn}? Its records stop updating immediately and its token cannot be recovered.`,
                          )
                        ) {
                          remove.mutate(host.id);
                        }
                      }}
                      disabled={remove.isPending}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!hosts.isLoading && !hosts.isError && hosts.data?.length === 0 && (
        <p className="muted">No dynamic hosts yet.</p>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (fqdn.trim()) create.mutate(fqdn);
        }}
      >
        <input
          value={fqdn}
          onChange={(event) => setFqdn(event.target.value)}
          placeholder="home.example.co.za"
          aria-label="Dynamic host FQDN"
        />
        <button type="submit" disabled={create.isPending || !fqdn.trim()}>
          Add dynamic host
        </button>
      </form>
      {create.isError && <p className="bad">{(create.error as Error).message}</p>}
    </div>
  );
}
